import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeeService } from '../service/fee.service';
import { CardOption, FeeMembershipResponse, IndustryItem, MemberPaymentRecord, MemberProfileResponse } from '../interface/fee.interface';
import { formatApiDateString } from '../utils/date.utils';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../service/navigation.service';
import {FEE_PAYMENT_STATUS, FEE_PAYMENT_STATUS_DEFAULT_ICON, FEE_PAYMENT_STATUS_ICON, FEE_PAYMENT_STATUS_LABEL_KEY, FeePaymentStatus} from '../constants/fee-payment-status.constants';
import { SnackBarUtil } from '../qr-copy-message/snack-bar-util.service';
import { MockupDataModel } from '../qr-copy-message/mockup-data.model';

type PaymentRowView = MemberPaymentRecord & {
  periodLabel: string;
};

@Component({
  selector: 'app-user-fee',
  templateUrl: './fee.component.html',
  styleUrls: ['./fee.component.scss']
})
export class UserFeeComponent implements OnInit, OnDestroy, AfterViewChecked {

  private readonly destroy$ = new Subject<void>();
  private readonly defaultIndustryIcon = 'assets/fee-icons/Placeholder.svg';
  private readonly industryIconByName: Record<string, string> = {
    accounting: 'accounting.svg',
    education: 'education.svg',
    health: 'health.svg',
    hospitality: 'hospitality.svg',
    other: 'other.svg',
    telecommunication: 'telecommunication.svg',
    telecommunications: 'telecommunication.svg'
  };
  private readonly industryTranslationKeyByName: Record<string, string> = {
    accounting: 'accounting',
    education: 'education',
    health: 'health_wellness',
    'health/wellness': 'health_wellness',
    hospitality: 'hospitality',
    other: 'other',
    telecommunication: 'telecommunications',
    telecommunications: 'telecommunications'
  };

  selectedIndex: number | null = null;
  groupName = '';
  imagePreviewUrl: string | null = null;
  selectedFile: File | null = null;
  loading = false;
  companyNameError = '';
  logoError = '';
  industries: IndustryItem[] = [];
  availableCards: CardOption[] = [];
  selectedCardId: number | null = null;

  showCreateView = false;

  memberships: FeeMembershipResponse[] = [];
  activeMembership: FeeMembershipResponse | null = null;
  memberProfile: MemberProfileResponse | null = null;
  profileLoading = false;
  activeSubgroupId: number | null = null;
  @ViewChild('tabsScroll') private tabsScrollRef?: ElementRef<HTMLDivElement>;
  tabsStartIndex = 0;
  tabsEndIndex = 0;
  hasMoreTabsRight = false;
  private readonly tabsGapPx = 10;
  selectedMembershipId: number | null = null;
  private selectedSubgroupId: number | null = null;
  payingRecordId: number | null = null;
  cancelingBinding = false;
  showSubgroupInfoMobile = false;
  readonly loadMorePageSize = 10;
  paymentRowsVisibleCount = this.loadMorePageSize;
  isLoading = false;

  readonly currencySymbol = '֏';

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly feeService: FeeService,
    private readonly translateService: TranslateService,
    private readonly navigationService: NavigationService,
    private readonly snackBar: SnackBarUtil,
    private readonly cdr: ChangeDetectorRef
  ) {}

  goBack(): void {
    this.navigationService.goBack();
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const createMode = params.get('create');
      this.showCreateView = createMode === '1' || createMode === 'true';
      const membershipIdParam = params.get('membershipId');
      const newMembershipId = membershipIdParam ? Number(membershipIdParam) || null : null;
      const membershipChanged = newMembershipId !== this.selectedMembershipId;
      this.selectedMembershipId = newMembershipId;

      const subgroupIdParam = params.get('subgroupId');
      this.selectedSubgroupId = subgroupIdParam ? Number(subgroupIdParam) || null : null;

      if (membershipChanged && this.memberships.length > 0 && this.selectedMembershipId !== null) {
        const selected = this.memberships.find(m => m.memberId === this.selectedMembershipId);
        if (selected) {
          this.selectMembership(selected);
        }
      }
    });

    this.feeService.getIndustries().pipe(takeUntil(this.destroy$)).subscribe(list => {
      this.industries = list;
    });
    this.feeService.getAvailableCards().pipe(takeUntil(this.destroy$)).subscribe(cards => {
      this.availableCards = cards;
    });
    this.feeService.getMyMemberships().pipe(takeUntil(this.destroy$)).subscribe(list => {
      this.memberships = list;

      if (this.showCreateView) {
        return;
      }

      if (list.length > 0) {
        if (this.selectedMembershipId !== null) {
          const selected = list.find(m => m.memberId === this.selectedMembershipId);
          if (selected) {
            this.selectMembership(selected);
          } else {
            this.router.navigate(['/error-page']);
          }
        } else {
          this.selectMembership(list[list.length - 1]);
        }
      } else if (this.selectedMembershipId !== null) {
        this.router.navigate(['/error-page']);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.isLoading) {
      this.loadMore();
    }
  }

  // ── Membership selection ──────────────────────────────────────────────────

  selectMembership(m: FeeMembershipResponse): void {
    this.activeMembership = m;
    this.memberProfile = null;
    this.profileLoading = true;
    this.resetPaymentRowsVisibleCount();
    this.feeService.getMemberProfile(m.memberId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: profile => {
          if (profile.active === false) {
            this.profileLoading = false;
            this.router.navigate(['/error-page']);
            return;
          }
          const visibleProfile = this.restrictToActiveSubgroups(profile);
          if (visibleProfile.subgroups.length === 0) {
            this.profileLoading = false;
            this.router.navigate(['/error-page']);
            return;
          }
          this.memberProfile = visibleProfile;
          this.activeSubgroupId = this.resolveActiveSubgroupId(visibleProfile);
          this.syncSubgroupQueryParam(this.activeSubgroupId);
          this.tabsStartIndex = 0;
          this.resetPaymentRowsVisibleCount();
          this.profileLoading = false;
        },
        error: () => {
          this.profileLoading = false;
        }
      });
  }

  getLogoUrl(logoPath: string | null | undefined): string | null {
    return this.feeService.getLogoUrl(logoPath);
  }

  subgroupNameFor(record: MemberPaymentRecord): string {
    if (!this.memberProfile || !record.subgroupId) return '';
    const sg = this.memberProfile.subgroups.find(s => s.id === record.subgroupId);
    return sg ? sg.name : '';
  }

  selectSubgroup(subgroupId: number): void {
    this.activeSubgroupId = subgroupId;
    this.selectedSubgroupId = subgroupId;
    this.showSubgroupInfoMobile = false;
    this.resetPaymentRowsVisibleCount();
    this.syncSubgroupQueryParam(subgroupId);
  }

  private restrictToActiveSubgroups(profile: MemberProfileResponse): MemberProfileResponse {
    const activeSubgroups = profile.subgroups.filter(sg => sg.active !== false);
    const activeSubgroupIds = new Set(activeSubgroups.map(sg => sg.id));
    return {
      ...profile,
      subgroups: activeSubgroups,
      paymentHistory: profile.paymentHistory.filter(
        record => record.subgroupId != null && activeSubgroupIds.has(record.subgroupId)
      )
    };
  }

  private resolveActiveSubgroupId(profile: MemberProfileResponse): number | null {
    if (profile.subgroups.length === 0) {
      return null;
    }
    if (this.selectedSubgroupId !== null
      && profile.subgroups.some(sg => sg.id === this.selectedSubgroupId)) {
      return this.selectedSubgroupId;
    }
    return profile.subgroups[0].id;
  }

  private syncSubgroupQueryParam(subgroupId: number | null): void {
    const current = this.route.snapshot.queryParamMap.get('subgroupId');
    const next = subgroupId === null ? null : String(subgroupId);
    if (current === next) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { subgroupId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  get activeSubgroup() {
    if (!this.memberProfile || this.activeSubgroupId === null) return null;
    return this.memberProfile.subgroups.find(s => s.id === this.activeSubgroupId) ?? null;
  }

  get showTabsNavigation(): boolean {
    return this.tabsStartIndex > 0 || this.hasMoreTabsRight;
  }

  nextPage(): void {
    if (this.hasMoreTabsRight) {
      this.tabsStartIndex = this.tabsEndIndex;
      this.tabsEndIndex = this.tabsStartIndex + 1;
    }
  }

  prevPage(): void {
    this.tabsStartIndex = 0;
  }

  ngAfterViewChecked(): void {
    const el = this.tabsScrollRef?.nativeElement;
    const total = this.memberProfile?.subgroups.length ?? 0;
    if (!el || total === 0) return;
    const tabs = Array.from(el.children) as HTMLElement[];
    if (tabs.length !== total) return;

    const available = el.clientWidth;
    let used = 0;
    let end = this.tabsStartIndex;
    for (let i = this.tabsStartIndex; i < total; i++) {
      const width = tabs[i].offsetWidth + (i > this.tabsStartIndex ? this.tabsGapPx : 0);
      if (used + width > available) break;
      used += width;
      end = i + 1;
    }
    end = Math.max(end, this.tabsStartIndex + 1);

    const hasRight = end < total;
    if (end !== this.tabsEndIndex || hasRight !== this.hasMoreTabsRight) {
      this.tabsEndIndex = end;
      this.hasMoreTabsRight = hasRight;
      this.cdr.detectChanges();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
  }

  get isScanMeUser(): boolean {
    return !!this.memberProfile?.userId;
  }

  get showPaymentModeCard(): boolean {
    return !!this.activeSubgroup;
  }

  get isBindingPaymentActive(): boolean {
    return !!this.activeSubgroup?.recurringPaymentEnabled
      || this.filteredPaymentHistory.some(record => !!record.recurringPaymentEnabled);
  }

  get paymentModeLabelKey(): string {
    return this.isBindingPaymentActive
      ? 'fee.payment_mode.automatic_binding'
      : 'fee.payment_mode.manual';
  }

  get paymentModeValueKey(): string {
    return this.isBindingPaymentActive
      ? 'fee.payment_mode.automatic_binding_value'
      : 'fee.payment_mode.manual_value';
  }

  get filteredPaymentHistory(): MemberPaymentRecord[] {
    if (!this.memberProfile) return [];
    if (this.activeSubgroupId === null) return this.memberProfile.paymentHistory;
    return this.memberProfile.paymentHistory.filter(record => record.subgroupId === this.activeSubgroupId);
  }

  get filteredPaymentRowsView(): PaymentRowView[] {
    return this.filteredPaymentHistory.map(row => ({
      ...row,
      periodLabel: row.month
    }));
  }

  get displayedPaymentRows(): PaymentRowView[] {
    return this.filteredPaymentRowsView.slice(0, this.paymentRowsVisibleCount);
  }

  get hasMorePaymentRows(): boolean {
    return this.paymentRowsVisibleCount < this.filteredPaymentRowsView.length;
  }

  loadMore(): void {
    if (this.hasMorePaymentRows) {
      this.paymentRowsVisibleCount += this.loadMorePageSize;
    }
  }

  onPayClick(record: MemberPaymentRecord): void {
    if (!this.activeMembership || this.payingRecordId !== null) return;
    this.payingRecordId = record.id;
    const originUrl = this.router.url;
    this.feeService.generateMemberPaymentToken(this.activeMembership.memberId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: token => {
          this.payingRecordId = null;
          sessionStorage.setItem('fee_back_url', originUrl);
          this.router.navigate(['/fee-payment', token, 'pay', record.id]);
        },
        error: () => {
          this.payingRecordId = null;
        }
      });
  }

  cancelRecurringBinding(): void {
    const memberId = this.memberProfile?.id;
    const subgroupId = this.activeSubgroup?.id;
    if (!memberId || !subgroupId || !this.isBindingPaymentActive || this.cancelingBinding) return;

    this.cancelingBinding = true;
    this.feeService.cancelRecurringBinding(memberId, subgroupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: profile => {
          this.cancelingBinding = false;
          this.memberProfile = profile;
          if (!profile.subgroups.find(s => s.id === this.activeSubgroupId)) {
            this.activeSubgroupId = profile.subgroups[0]?.id ?? null;
          }
          this.showSnackbar(
            MockupDataModel.success(this.translateService.instant('fee.messages.cancel_binding_payment_success')),
            SnackBarUtil.CLASSES.success
          );
        },
        error: () => {
          this.cancelingBinding = false;
        }
      });
  }

  isPaidRecord(record: MemberPaymentRecord): boolean {
    return (record.status || '').toUpperCase() === 'PAID';
  }

  isPendingRecord(record: MemberPaymentRecord): boolean {
    return (record.status || '').toUpperCase() === 'PENDING';
  }

  canPayRecord(record: MemberPaymentRecord): boolean {
    if (!this.isPendingRecord(record) || record.id <= 0 || (record.due ?? 0) <= 0) {
      return false;
    }
    if (record.manualPaymentAllowed) {
      return true;
    }
    return !record.recurringPaymentEnabled;
  }

  formatDate(d: string | undefined): string {
    return formatApiDateString(d, '/');
  }

  frequencyLabel(value: string | undefined): string {
    if (!value) return '';
    const key = `fee.frequency_types.${value.toUpperCase()}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : value;
  }

  formatDateRange(subgroup: { startDate?: string; endDate?: string }): string {
    if (!subgroup.startDate) return '—';
    const from = this.formatDate(subgroup.startDate);
    return subgroup.endDate ? `${from} - ${this.formatDate(subgroup.endDate)}` : from;
  }

  discountLabel(subgroup: { discountType?: string; discountValue?: number }): string {
    if (!subgroup.discountValue) return '—';
    return subgroup.discountType === 'PERCENT'
      ? `${subgroup.discountValue}%`
      : `${subgroup.discountValue} ${this.currencySymbol}`;
  }

  paymentDiscountLabel(record: MemberPaymentRecord): string {
    if (!record.discount) return '—';
    return record.discountType === 'PERCENT'
      ? `${record.discount}%`
      : `${record.discount} ${this.currencySymbol}`;
  }

  paymentStatusClass(status: string): string {
    return 'status-' + (status || '').toLowerCase();
  }

  paymentStatusIcon(record: MemberPaymentRecord): string {
    if (this.isPaidRecord(record)) return FEE_PAYMENT_STATUS_ICON[FEE_PAYMENT_STATUS.PAID];

    const status = this.normalizePaymentStatus(record.status);
    return FEE_PAYMENT_STATUS_ICON[status] ?? FEE_PAYMENT_STATUS_DEFAULT_ICON;
  }

  paymentStatusLabel(record: MemberPaymentRecord): string {
    const status = this.isPaidRecord(record)
      ? FEE_PAYMENT_STATUS.PAID
      : this.normalizePaymentStatus(record.status);
    const key = FEE_PAYMENT_STATUS_LABEL_KEY[status];
    return key ? this.translateService.instant(key) : this.toTitleCase(record.status || '');
  }

  paymentStatusViewClass(record: MemberPaymentRecord): string {
    if (this.isPaidRecord(record)) return 'status-completed';
    const status = (record.status || '').toString().toLowerCase();
    return status ? `status-${status}` : 'status-unknown';
  }

  getIndustryIconSrc(industry: IndustryItem): string {
    const image = (industry.image || '').trim();
    if (image) {
      if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('assets/')) {
        return image;
      }
      const withExtension = image.includes('.') ? image : `${image}.svg`;
      return `assets/fee-icons/${withExtension}`;
    }

    const key = (industry.name || '').trim().toLowerCase();
    const mapped = this.industryIconByName[key];
    return mapped ? `assets/fee-icons/${mapped}` : this.defaultIndustryIcon;
  }

  onIndustryIconError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (!img || img.src.endsWith('/assets/fee-icons/Placeholder.svg')) {
      return;
    }
    img.src = this.defaultIndustryIcon;
  }

  getIndustrySubtitle(industry: IndustryItem): string {
    const key = this.getIndustryTranslationKey(industry);
    return key
      ? this.translateService.instant(`fee.industry_subtitles.${key}`)
      : industry.description || '';
  }

  getIndustryName(industry: IndustryItem): string {
    const key = this.getIndustryTranslationKey(industry);
    return key
      ? this.translateService.instant(`fee.industry_names.${key}`)
      : industry.name || '';
  }

  private getIndustryTranslationKey(industry: IndustryItem): string | null {
    const name = (industry.name || '')
      .trim()
      .toLowerCase()
      .replace(/\s*\/\s*/g, '/')
      .replace(/\s+/g, ' ');
    return this.industryTranslationKeyByName[name] ?? null;
  }

  private toTitleCase(value: string): string {
    return value
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  private normalizePaymentStatus(status: string | undefined): FeePaymentStatus {
    return (status || '').toString().toUpperCase() as FeePaymentStatus;
  }

  private resetPaymentRowsVisibleCount(): void {
    this.paymentRowsVisibleCount = this.loadMorePageSize;
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data,
    });
  }

  // ── Create-industry actions ───────────────────────────────────────────────

  selectCard(index: number): void {
    this.selectedIndex = index;
    this.groupName = '';
    this.imagePreviewUrl = null;
    this.selectedFile = null;
    this.companyNameError = '';
    this.logoError = '';
    this.selectedCardId = null;
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.logoError = '';
    if (file.size > 5 * 1024 * 1024) {
      this.logoError = 'fee.logo_too_large';
      input.value = '';
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!['jpg', 'jpeg', 'heic', 'png'].includes(ext)) {
      this.logoError = 'fee.logo_format';
      input.value = '';
      return;
    }
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = () => { this.imagePreviewUrl = reader.result as string; };
    reader.readAsDataURL(file);
  }

  cancel(): void {
    this.selectedIndex = null;
    this.groupName = '';
    this.imagePreviewUrl = null;
    this.selectedFile = null;
    this.companyNameError = '';
    this.logoError = '';
    this.selectedCardId = null;
  }

  save(): void {
    this.companyNameError = '';
    this.logoError = '';
    if (this.selectedIndex === null) return;
    if (!this.groupName.trim()) {
      this.companyNameError = 'fee.company_name_required';
      return;
    }
    if (this.groupName.trim().length > 20) {
      this.companyNameError = 'fee.company_name_max';
      return;
    }
    const industry = this.industries[this.selectedIndex];
    const fd = new FormData();
    fd.append('industryId', String(industry.id));
    fd.append('companyName', this.groupName.trim());
    if (this.selectedFile) fd.append('logo', this.selectedFile);
    if (this.selectedCardId !== null) fd.append('userCardId', String(this.selectedCardId));
    this.loading = true;
    this.feeService.createUserIndustry(fd).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.loading = false;
        this.showCreateView = false;
        this.router.navigate(['/fees', response.id], { queryParams: { new: '1' } });
      },
      error: () => { this.loading = false; }
    });
  }
}
